/**
 * Техкарты-варианты сезона — экран 2(б) годового планирования.
 *
 * Макет (утверждён founder 15.08):
 *   `docs/superpowers/prototypes/annual-planning/v2-05-techcard.html`
 *   (ветка `docs/wireframes-season-planning-v2`, скриншот рядом).
 * Модель и инварианты: ADR-066 (`docs/superpowers/adr/2026-08-15-adr-066-tech-card-variant.md`).
 *
 * Что экран обещает пользователю:
 *   • вариант техкарты — атом бюджета сезона: «Интенсив», «Базовый»,
 *     «Эконом-защита» на одну культуру, у каждого свой ₽/га;
 *   • блоки (обработка почвы / сев / питание / защита / уборка) — сменные
 *     наборы операций: меняешь блок — деньги пересчитываются ДО сохранения;
 *   • карта не принадлежит варианту плана — один вариант назначается
 *     нескольким (ADR-066 D2).
 *
 * Чего здесь пока НЕТ и почему (честно, без заглушек-обманок):
 *   • правки строк операций — отдельный шаг, экран сейчас читающий + замена
 *     блоков и клонирование;
 *   • выручки и маржи — нет плана по выручке (этап 3), считать её не из чего;
 *   • названий сцепок — нет справочника парка техники (экран «Техника», v2-12);
 *   • советчика «эконом-защита → −3 ц/га» — ADR советчика ещё не реализован.
 */
import { useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { useCan } from '@/lib/auth/usePermissions';
import { toast } from '@/lib/toast';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';
import {
  CARD_STATUS_TITLES,
  blockTitle,
  blocksOfCard,
  formatNumber,
  formatPerHa,
  replacementSources,
  techCardErrorText,
  useAddOperation,
  useAddStage,
  useApplyBlockReplace,
  useAssignTechCard,
  useCloneTechCard,
  useCropNames,
  useDeleteOperation,
  useDeleteStage,
  usePatchOperation,
  usePatchStage,
  useReorderStages,
  useSetDirectCosts,
  useSetLabor,
  useSetMaterials,
  useTechCard,
  useTechCardCost,
  useTechCards,
  useUnassignTechCard,
  type BlockKind,
  type TechCard,
  type TechCardOperation,
} from '@/lib/planning/techCards';
import { AssignPlanVariantsDialog } from './AssignPlanVariantsDialog';
import { CalcContextPicker } from './CalcContextPicker';
import { LaborDialog } from './LaborDialog';
import { MaterialsDialog } from './MaterialsDialog';
import { MonthlyReportBlock } from './MonthlyReportBlock';
import { ProcurementBlock } from './ProcurementBlock';
import { OperationDialog, type OperationDialogSubmit } from './OperationDialog';
import { BlockReplacePanel } from './BlockReplacePanel';
import { CloneVariantDialog } from './CloneVariantDialog';
import { CreateCardButton } from './CreateCardButton';
import { CostSummary } from './CostSummary';
import { OperationsTable, type OperationRowActions } from './OperationsTable';
import { StagesPanel, type StagesPanelActions } from './StagesPanel';
import { SystemsBlock } from './SystemsBlock';
import { VariantsList } from './VariantsList';

export function TechCardsPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const search = useSearch({ strict: false }) as { card?: string; calc?: string };
  const navigate = useNavigate();
  const season = useSeasonByYear(year);

  const canRead = useCan('tech_cards', 'r');
  const canCreate = useCan('tech_cards', 'c');
  const canWrite = useCan('tech_cards', 'u');

  const cards = useTechCards(canRead ? season.seasonId : null);
  const openCardId = search.card ?? null;
  const detail = useTechCard(canRead ? openCardId : null);
  // Точечный резолв культур: идентификаторы берём с самого экрана — список
  // вариантов плюс открытая карта с её предшественником.
  const cropNames = useCropNames([
    ...(cards.data ?? []).map((c) => c.crop_id),
    detail.data?.crop_id,
    // Предшественники — множество (ADR-071 D2): резолвим имена всех.
    ...(detail.data?.predecessor_crop_ids ?? []),
    detail.data?.predecessor_crop_id,
  ]);

  const [activeBlock, setActiveBlock] = useState<BlockKind | null>(null);
  const [cloneFor, setCloneFor] = useState<TechCard | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  // Правка строк: одна форма на «новую» и «существующую» работу — состав полей
  // у них один, разница только в типе работы (у новой он задаётся).
  const [editOp, setEditOp] = useState<
    { operation: TechCardOperation | null; blockKind: BlockKind } | null
  >(null);
  const [materialsFor, setMaterialsFor] = useState<TechCardOperation | null>(null);
  const [laborFor, setLaborFor] = useState<TechCardOperation | null>(null);
  const [deleteOp, setDeleteOp] = useState<TechCardOperation | null>(null);

  const clone = useCloneTechCard();
  const applyReplace = useApplyBlockReplace();
  const assign = useAssignTechCard();
  const unassign = useUnassignTechCard();
  const openCardIdForOps = detail.data?.id ?? null;
  const addOperation = useAddOperation(openCardIdForOps);
  const patchOperation = usePatchOperation(openCardIdForOps);
  const deleteOperation = useDeleteOperation(openCardIdForOps);
  const setMaterials = useSetMaterials(openCardIdForOps);
  const setLabor = useSetLabor(openCardIdForOps);
  const setDirectCosts = useSetDirectCosts(openCardIdForOps);
  // Этапы карты (ADR-071 D3) — свободные имена, порядок drag'ом.
  const addStage = useAddStage(openCardIdForOps);
  const patchStage = usePatchStage(openCardIdForOps);
  const deleteStage = useDeleteStage(openCardIdForOps);
  const reorderStages = useReorderStages(openCardIdForOps);
  const stagesBusy =
    addStage.isPending || patchStage.isPending || deleteStage.isPending || reorderStages.isPending;
  const stagesActions: StagesPanelActions = {
    onAdd: (name) => addStage.mutate({ name }),
    onRename: (stageId, name) => patchStage.mutate({ stageId, name }),
    onDelete: (stageId) => deleteStage.mutate({ stageId }),
    onReorder: (ids) => reorderStages.mutate({ ids }),
  };

  // Расчётный контекст (ADR-071 D1) живёт в адресе: ссылку «карта на площади
  // варианта» можно переслать экономисту.
  const calcScenarioId = search.calc ?? null;
  const pickCalc = (scenarioId: string | null) =>
    void navigate({
      to: '/planning/seasons/$year/tech-cards',
      params: { year: String(year) },
      search: scenarioId ? { card: openCardId ?? undefined, calc: scenarioId } : { card: openCardId ?? undefined },
    });
  const openCard = (card: TechCard) => {
    setActiveBlock(null);
    void navigate({ to: '/planning/seasons/$year/tech-cards', params: { year: String(year) }, search: { card: card.id } });
  };
  const backToList = () => {
    setActiveBlock(null);
    void navigate({ to: '/planning/seasons/$year/tech-cards', params: { year: String(year) }, search: {} });
  };

  const card = detail.data ?? null;
  const title = season.validYear ? `Технологические карты · сезон ${year}` : 'Технологические карты';

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-[1600px] pb-safe-fab">
        <PageHeader
          title={title}
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            // Средняя крошка ведёт в хаб бюджета сезона (макет v2-00).
            {
              label: season.validYear ? `Бюджет сезона ${year}` : 'Бюджет сезона',
              ...(season.validYear ? { to: `/planning/seasons/${year}/budget` } : {}),
            },
            { label: 'Технологические карты' },
          ]}
          description="Вариант техкарты — атом бюджета: нормирует культуру, а не поле. Замена блока пересчитывает ₽/га до сохранения."
        />

        <Stack gap={4} className="mt-4">
          {!season.validYear && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Год в адресе страницы некорректный.
              </CardContent>
            </Card>
          )}

          {season.validYear && !canRead && (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                Технологические карты вам не открыты. Доступ к плановой себестоимости даёт
                администратор хозяйства — попросите роль с правом на техкарты.
              </CardContent>
            </Card>
          )}

          {season.validYear && canRead && (
            <>
              {season.isLoading && <Skeleton className="h-96 w-full" />}

              {season.isError && (
                <Card>
                  <CardContent className="p-4 text-sm text-destructive">
                    Не удалось загрузить сезоны. Обновите страницу.
                  </CardContent>
                </Card>
              )}

              {/* AK-648/AK-649: «сезона нет в окне» и «сезон не заведён» —
                  разные причины и разные действия; пустого экрана (season_id
                  null при найденной карточке) больше не остаётся. */}
              {season.missing && <SeasonYearOutOfWindowCard year={year} />}
              {season.notOpened && (
                <SeasonNotStartedCard year={year} onOpened={() => undefined} />
              )}

              {season.seasonId && !openCardId && (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Варианты техкарт сезона {year}: {(cards.data ?? []).length}
                    </span>
                    <span className="ml-auto">
                      <CreateCardButton
                        seasonId={season.seasonId}
                        seasonYear={year}
                        onCreated={(cardId) =>
                          void navigate({
                            to: '/planning/seasons/$year/tech-cards',
                            params: { year: String(year) },
                            search: { card: cardId },
                          })
                        }
                      />
                    </span>
                  </div>
                  <ListView
                    loading={cards.isLoading}
                    error={cards.isError ? techCardErrorText(cards.error) : null}
                    cards={cards.data ?? []}
                    cropNames={cropNames.names}
                    canClone={canCreate}
                    year={year}
                    seasonId={season.seasonId}
                    onOpen={openCard}
                    onClone={setCloneFor}
                    onCreated={(cardId) =>
                      void navigate({
                        to: '/planning/seasons/$year/tech-cards',
                        params: { year: String(year) },
                        search: { card: cardId },
                      })
                    }
                  />
                </>
              )}

              {season.seasonId && openCardId && (
                <>
                  <div>
                    <Button variant="ghost" size="sm" onClick={backToList}>
                      ← Все варианты сезона
                    </Button>
                  </div>

                  {detail.isLoading && <Skeleton className="h-96 w-full" />}

                  {detail.isError && (
                    <Card>
                      <CardContent className="p-4 text-sm text-destructive">
                        {techCardErrorText(detail.error)}
                      </CardContent>
                    </Card>
                  )}

                  {card && (
                    <CardDetail
                      card={card}
                      allCards={cards.data ?? []}
                      cropNames={cropNames.names}
                      seasonId={season.seasonId}
                      calcScenarioId={calcScenarioId}
                      onPickCalc={pickCalc}
                      canWrite={canWrite}
                      canCreate={canCreate}
                      activeBlock={activeBlock}
                      onPickBlock={(kind) => setActiveBlock((cur) => (cur === kind ? null : kind))}
                      applying={applyReplace.isPending}
                      onApplyReplace={({ sourceCardId, asNewCardName }) => {
                        if (!activeBlock) return;
                        applyReplace.mutate(
                          { cardId: card.id, blockKind: activeBlock, sourceCardId, asNewCardName },
                          {
                            onSuccess: (result) => {
                              setActiveBlock(null);
                              toast.success(
                                asNewCardName
                                  ? `Новый вариант «${asNewCardName}» создан со сменённым блоком «${result.block_title}»`
                                  : `Блок «${result.block_title}» заменён`,
                              );
                              // Ушли в новый вариант — открываем его: иначе
                              // пользователь смотрит на старую карту и думает,
                              // что замена не сработала.
                              if (result.tech_card_id !== card.id) {
                                void navigate({
                                  to: '/planning/seasons/$year/tech-cards',
                                  params: { year: String(year) },
                                  search: { card: result.tech_card_id },
                                });
                              }
                            },
                          },
                        );
                      }}
                      onClone={() => setCloneFor(card)}
                      onAssign={() => setAssignOpen(true)}
                      stagesBusy={stagesBusy}
                      stagesActions={stagesActions}
                      rowActions={{
                        onAddOperation: (kind) => setEditOp({ operation: null, blockKind: kind }),
                        onEditOperation: (op) =>
                          setEditOp({
                            operation: op,
                            blockKind: (op.block_kind as BlockKind | null) ?? 'other',
                          }),
                        onEditMaterials: setMaterialsFor,
                        onEditLabor: setLaborFor,
                        onDeleteOperation: setDeleteOp,
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </div>

      {cloneFor && (
        <CloneVariantDialog
          card={cloneFor}
          saving={clone.isPending}
          onSubmit={(name) =>
            clone.mutate(
              { cardId: cloneFor.id, name },
              {
                onSuccess: (created) => {
                  setCloneFor(null);
                  clone.reset();
                  toast.success(`Вариант «${created.name}» создан`);
                  openCard(created);
                },
              },
            )
          }
          onClose={() => {
            setCloneFor(null);
            clone.reset();
          }}
        />
      )}

      {assignOpen && card && season.seasonId && (
        <AssignPlanVariantsDialog
          card={card}
          seasonId={season.seasonId}
          canAssign={canWrite}
          busy={assign.isPending || unassign.isPending}
          onAssign={(scenarioId) => assign.mutate({ scenarioId, techCardId: card.id })}
          onUnassign={(scenarioId) => unassign.mutate({ scenarioId, techCardId: card.id })}
          onClose={() => setAssignOpen(false)}
        />
      )}

      {editOp && card && (
        <OperationDialog
          operation={editOp.operation}
          blockKind={editOp.blockKind}
          seasonId={season.seasonId}
          stages={card.stages ?? []}
          parentCandidates={card.operations.filter((op) => !op.parent_operation_id)}
          saving={addOperation.isPending || patchOperation.isPending}
          onSubmit={(values: OperationDialogSubmit) => {
            // Диалог закрываем только на успехе: при отказе бэка (toast.error)
            // введённое остаётся в форме, а не пропадает вместе с окном.
            const done = () => {
              setEditOp(null);
              addOperation.reset();
              patchOperation.reset();
            };
            if (values.create) {
              addOperation.mutate(values.create, {
                onSuccess: () => {
                  done();
                  toast.success('Операция добавлена — ₽/га пересчитан');
                },
              });
            } else if (values.patch && editOp.operation) {
              patchOperation.mutate(
                { opId: editOp.operation.id, patch: values.patch },
                {
                  onSuccess: () => {
                    done();
                    toast.success('Операция сохранена — ₽/га пересчитан');
                  },
                },
              );
            }
          }}
          onClose={() => {
            setEditOp(null);
            addOperation.reset();
            patchOperation.reset();
          }}
        />
      )}

      {materialsFor && card && season.seasonId && (
        <MaterialsDialog
          operation={materialsFor}
          seasonId={season.seasonId}
          cropId={card.crop_id}
          saving={setMaterials.isPending}
          onSubmit={(materials) =>
            setMaterials.mutate(
              { opId: materialsFor.id, materials },
              {
                onSuccess: () => {
                  setMaterialsFor(null);
                  setMaterials.reset();
                  toast.success('Материалы сохранены — ₽/га пересчитан');
                },
              },
            )
          }
          onClose={() => {
            setMaterialsFor(null);
            setMaterials.reset();
          }}
        />
      )}

      {laborFor && card && (
        <LaborDialog
          operation={laborFor}
          saving={setLabor.isPending || setDirectCosts.isPending}
          onSubmit={({ labor, directCosts }) =>
            setLabor.mutate(
              { opId: laborFor.id, labor },
              {
                onSuccess: () =>
                  setDirectCosts.mutate(
                    { opId: laborFor.id, directCosts },
                    {
                      onSuccess: () => {
                        setLaborFor(null);
                        setLabor.reset();
                        setDirectCosts.reset();
                        toast.success('Оплата труда сохранена — деньги строки пересчитаны');
                      },
                    },
                  ),
              },
            )
          }
          onClose={() => {
            setLaborFor(null);
            setLabor.reset();
            setDirectCosts.reset();
          }}
        />
      )}

      {deleteOp && card && (
        <ConfirmDeleteOperation
          operation={deleteOp}
          busy={deleteOperation.isPending}
          onConfirm={() =>
            deleteOperation.mutate(
              { opId: deleteOp.id },
              {
                onSuccess: () => {
                  setDeleteOp(null);
                  toast.success('Операция убрана из варианта — ₽/га пересчитан');
                },
              },
            )
          }
          onClose={() => setDeleteOp(null)}
        />
      )}
    </AppShell>
  );
}

/**
 * Удаление строки подтверждаем: вместе с работой уходят её материалы и её
 * свита, а «отменить» у этого действия нет — в отличие от замены блока, где
 * можно уйти в новый вариант.
 */
function ConfirmDeleteOperation({
  operation,
  busy,
  onConfirm,
  onClose,
}: {
  operation: TechCardOperation;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const label = operation.name ?? operation.op_type_name ?? 'операция без названия';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Убрать операцию «{label}» из варианта?</DialogTitle>
          <DialogDescription>
            Вместе с операцией уйдут её материалы и вспомогательные работы под ней. Другие
            варианты этой культуры не изменятся.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Оставить
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy}>
            Убрать операцию
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ListView({
  loading,
  error,
  cards,
  cropNames,
  canClone,
  year,
  seasonId,
  onOpen,
  onClone,
  onCreated,
}: {
  loading: boolean;
  error: string | null;
  cards: TechCard[];
  cropNames: Record<string, string>;
  canClone: boolean;
  year: number;
  seasonId: string | null;
  onOpen: (card: TechCard) => void;
  onClone: (card: TechCard) => void;
  onCreated: (cardId: string) => void;
}) {
  if (loading) return <Skeleton className="h-64 w-full" />;
  if (error) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
      </Card>
    );
  }
  if (cards.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm">
          <p className="text-foreground">В бюджете сезона {year} ещё нет ни одной техкарты.</p>
          <p className="mt-2 text-muted-foreground">
            Техкарту заводят на культуру: «Озимая пшеница по гороху, 50 ц/га». Дальше её
            клонируют вариантами — интенсив, базовый, эконом — и сравнивают по ₽/га.
          </p>
          <div className="mt-4 flex justify-center">
            <CreateCardButton
              emptyState
              seasonId={seasonId}
              seasonYear={year}
              onCreated={onCreated}
            />
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <VariantsList
      cards={cards}
      cropNames={cropNames}
      onOpen={onOpen}
      onClone={onClone}
      canClone={canClone}
    />
  );
}

export function CardDetail({
  card,
  allCards,
  cropNames,
  seasonId = null,
  calcScenarioId = null,
  onPickCalc,
  canWrite,
  canCreate,
  activeBlock,
  onPickBlock,
  applying,
  onApplyReplace,
  onClone,
  onAssign,
  stagesBusy = false,
  stagesActions,
  rowActions,
}: {
  card: TechCard;
  allCards: TechCard[];
  cropNames: Record<string, string>;
  seasonId?: string | null;
  /** Вариант плана, на площадь которого считаем деньги (ADR-071 D1). */
  calcScenarioId?: string | null;
  onPickCalc?: (scenarioId: string | null) => void;
  canWrite: boolean;
  canCreate: boolean;
  activeBlock: BlockKind | null;
  onPickBlock: (kind: BlockKind) => void;
  applying: boolean;
  onApplyReplace: (input: { sourceCardId: string; asNewCardName: string | null }) => void;
  onClone: () => void;
  onAssign: () => void;
  stagesBusy?: boolean;
  stagesActions?: StagesPanelActions;
  rowActions: OperationRowActions;
}) {
  // Деньги в контексте варианта: тот же движок, площадь — из назначения.
  const scenarioCost = useTechCardCost(card.id, calcScenarioId);
  const displayCost = (calcScenarioId ? scenarioCost.data : null) ?? card.cost;
  const cropName = cropNames[card.crop_id] ?? 'культура без названия в справочнике';
  // Предшественники — множество (ADR-071 D2): перечисляем все, а не первый.
  const predecessorIds = card.predecessor_crop_ids ?? (card.predecessor_crop_id ? [card.predecessor_crop_id] : []);
  const predecessor = predecessorIds
    .map((id) => cropNames[id])
    .filter(Boolean)
    .join(', ');
  const blocks = blocksOfCard(card.operations);
  const siblings = allCards.filter((c) => c.crop_id === card.crop_id && c.id !== card.id);

  return (
    <Stack gap={4}>
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-foreground">
              {cropName}
              {predecessor ? ` · предшественник: ${predecessor.toLowerCase()}` : ''} · вариант «
              {card.name}»
            </h2>
            <Badge variant={card.status === 'ready' ? 'primary' : 'outline'}>
              {CARD_STATUS_TITLES[card.status]}
            </Badge>
            <span className="ml-auto text-lg font-semibold text-foreground">
              {formatPerHa(displayCost?.per_ha ?? null)}
            </span>
          </div>
          <div className="flex min-h-10 flex-wrap items-center gap-x-6 gap-y-1 py-2 text-sm leading-6 text-muted-foreground">
            <span>
              {card.target_yield_c_ha === null
                ? 'плановая урожайность не задана'
                : `плановая урожайность ${formatNumber(card.target_yield_c_ha)} ц/га`}
              {card.marketable_yield_c_ha !== null
                ? ` · зачёт ${formatNumber(card.marketable_yield_c_ha)}`
                : ''}
            </span>
            <span>
              {card.scenarios_count > 0
                ? `назначен вариантам плана: ${card.scenarios_count}`
                : 'ни одному варианту плана не назначен'}
            </span>
            <span>операций в карте: {card.operations.length}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {canWrite && (
              <Button size="sm" variant="outline" onClick={onAssign}>
                Назначить вариантам плана
              </Button>
            )}
            {canCreate && (
              <Button size="sm" variant="outline" onClick={onClone}>
                Клонировать вариант
              </Button>
            )}
            {siblings.length > 0 && (
              <span className="text-xs text-muted-foreground">
                у этой культуры ещё{' '}
                {siblings.length === 1 ? '1 вариант' : `${siblings.length} варианта`} — блоки
                можно брать оттуда
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">
              Блоки техкарты — сменные наборы операций
            </h3>
            <span className="text-xs text-muted-foreground">
              {canWrite
                ? 'нажмите плитку блока, чтобы подобрать ему замену'
                : 'сколько стоит каждый блок; подбор замены доступен с правом правки техкарт'}
            </span>
          </div>
          {blocks.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">
              В варианте нет операций — блоков пока нет.
            </p>
          ) : (
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {blocks.map((kind) => {
                const count = card.operations.filter(
                  (op) => (op.block_kind ?? 'other') === kind,
                ).length;
                const active = kind === activeBlock;
                const body = (
                  <>
                    <div className="text-sm font-medium text-foreground">{blockTitle(kind)}</div>
                    <div className="text-xs text-muted-foreground">
                      {count === 1 ? '1 операция' : `${count} операций`}
                    </div>
                    <div className="mt-2 text-base font-semibold text-foreground">
                      {formatPerHa(card.cost?.by_block?.[kind] ?? null)}
                    </div>
                  </>
                );
                // Читателю плитка — цифра, а не кнопка. Предпросмотр замены на
                // бэке закрыт правом `tech_cards:U` целиком (гейт стоит ДО
                // разбора dry_run), поэтому кликабельная плитка привела бы
                // читателя ровно к красному отказу — обещание, которого API
                // не выполнит (вердикт head-web по этому PR).
                if (!canWrite) {
                  return (
                    <div
                      key={kind}
                      className="rounded-md border border-border bg-background p-3 text-left"
                    >
                      {body}
                    </div>
                  );
                }
                return (
                  <button
                    key={kind}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onPickBlock(kind)}
                    className={
                      active
                        ? 'rounded-md border border-primary bg-primary/10 p-3 text-left transition-colors'
                        : 'rounded-md border border-border bg-background p-3 text-left transition-colors hover:bg-accent'
                    }
                  >
                    {body}
                  </button>
                );
              })}
            </div>
          )}

          {activeBlock && canWrite && (
            <div className="mt-4">
              <BlockReplacePanel
                card={card}
                blockKind={activeBlock}
                sources={replacementSources(allCards, card, activeBlock)}
                applying={applying}
                onApply={onApplyReplace}
                onClose={() => onPickBlock(activeBlock)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Системы (AK-773, Р35) — вставленные мини-техкарты защиты / питания /
          обработки почвы. Стоят ПОСЛЕ плиток блоков и ДО таблицы операций:
          сначала «что за наборы стоят в карте», потом строки, из которых они
          состоят. */}
      <SystemsBlock
        cardId={card.id}
        seasonId={seasonId}
        cropId={card.crop_id}
        canWrite={canWrite}
      />

      <Card>
        <CardContent className="p-4">
          {/*
            Действия ЖИВУТ В ЗАГОЛОВКЕ СЕКЦИИ, а не только в серой строке блока
            внутри таблицы: у таблицы 11 колонок и горизонтальный скролл, и
            кнопки в строке блока уезжали за правый край — founder их не нашёл
            (P1 20.08). В строке блока они остались дублем для того, кто уже
            смотрит на нужный блок.
          */}
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">Операции варианта</h3>
            {canWrite && (
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => rowActions.onAddOperation(blocks[0] ?? 'tillage')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Добавить операцию
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPickBlock(activeBlock ?? blocks[0] ?? 'tillage')}
                  disabled={blocks.length === 0}
                >
                  Заменить блок
                </Button>
              </div>
            )}
          </div>
          <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
            Строка содержит всё, что нужно агроному и экономисту: кратность и объём работ,
            агроокно, норму выработки, оплату труда, топливо и материалы с нормой расхода.
            Вспомогательные работы идут свитой под основной операцией.
          </p>
          {stagesActions && (
            <div className="mt-3 rounded-md border border-border bg-background p-3">
              <StagesPanel
                stages={card.stages ?? []}
                canWrite={canWrite}
                busy={stagesBusy}
                actions={stagesActions}
              />
            </div>
          )}
          <div className="mt-3">
            <OperationsTable
              operations={card.operations}
              cost={displayCost}
              stages={card.stages ?? []}
              canReplace={canWrite}
              activeBlock={activeBlock}
              onReplaceBlock={onPickBlock}
              actions={rowActions}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="text-base font-semibold text-foreground">Экономика варианта</h3>
          <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
            Считается по бюджетным справочникам сезона — плановым ценам ТМЦ, расценкам работ
            и топливу. Правка цены в справочнике сразу меняет цифры здесь.
          </p>
          {seasonId && onPickCalc && (
            <div className="mt-3">
              <CalcContextPicker
                card={card}
                seasonId={seasonId}
                scenarioId={calcScenarioId}
                onPick={onPickCalc}
              />
            </div>
          )}
          <div className="mt-3">
            <CostSummary cost={displayCost} />
          </div>
        </CardContent>
      </Card>

      {calcScenarioId && (
        <MonthlyReportBlock scope={{ kind: 'card', cardId: card.id, scenarioId: calcScenarioId }} />
      )}
      {calcScenarioId && (
        <ProcurementBlock scope={{ kind: 'card', cardId: card.id, scenarioId: calcScenarioId }} />
      )}
    </Stack>
  );
}
